import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Search, User, Truck, Building2, Headphones, MapPin, 
  Shield, CheckCircle, Clock, ChevronLeft, ChevronRight, X,
  Mail, Phone, Hash, Crown, Calendar, Briefcase, FileText, Star, Globe, RefreshCw,
  GraduationCap, Wrench, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/media';
import logger from '../utils/logger';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Members' },
  { value: 'driver', label: 'Drivers', icon: Truck, color: 'text-blue-600', bg: 'bg-blue-50' },
  { value: 'carrier_owner', label: 'Carriers', icon: Building2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { value: 'dispatcher', label: 'Dispatchers', icon: Headphones, color: 'text-violet-600', bg: 'bg-violet-50' },
  { value: 'learner', label: 'Learners', icon: GraduationCap, color: 'text-orange-600', bg: 'bg-orange-50' },
  { value: 'service_provider', label: 'Service Providers', icon: Wrench, color: 'text-sky-600', bg: 'bg-sky-50' },
  { value: 'affiliate', label: 'Affiliates', icon: Sparkles, color: 'text-fuchsia-600', bg: 'bg-fuchsia-50' }
];

const ROLE_STYLES = {
  driver: { label: 'Driver', color: 'text-blue-700', bg: 'bg-blue-50', icon: Truck },
  carrier_owner: { label: 'Carrier Owner', color: 'text-emerald-700', bg: 'bg-emerald-50', icon: Building2 },
  dispatcher: { label: 'Dispatcher', color: 'text-violet-700', bg: 'bg-violet-50', icon: Headphones },
  learner: { label: 'Learner', color: 'text-orange-700', bg: 'bg-orange-50', icon: GraduationCap },
  service_provider: { label: 'Service Provider', color: 'text-sky-700', bg: 'bg-sky-50', icon: Wrench },
  affiliate: { label: 'Affiliate', color: 'text-fuchsia-700', bg: 'bg-fuchsia-50', icon: Sparkles }
};

const VERIFICATION_STYLES = {
  approved: { label: 'Verified', color: 'text-green-700', bg: 'bg-green-50', icon: CheckCircle },
  verified: { label: 'Verified', color: 'text-green-700', bg: 'bg-green-50', icon: CheckCircle },
  pending: { label: 'Pending', color: 'text-yellow-700', bg: 'bg-yellow-50', icon: Clock },
  unverified: { label: 'Unverified', color: 'text-slate-500', bg: 'bg-slate-50', icon: Shield }
};

const Members = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberProfile, setMemberProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '12' });
      if (searchTerm) params.set('q', searchTerm);
      if (locationSearch) params.set('location', locationSearch);
      if (roleFilter !== 'all') params.set('role', roleFilter);
      const res = await api.get(`/members/search?${params}`);
      setMembers(res.data.members || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (error) {
      logger.error('Members fetch error:', error);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, locationSearch, roleFilter]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleSearch = (val) => { setSearchTerm(val); setPage(1); };
  const handleLocationSearch = (val) => { setLocationSearch(val); setPage(1); };
  const handleRoleFilter = (val) => { setRoleFilter(val); setPage(1); };

  // Lock body scroll when popup is open
  useEffect(() => {
    if (selectedMember) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedMember]);

  const openMemberProfile = async (member) => {
    setSelectedMember(member);
    setMemberProfile(null);
    setProfileLoading(true);
    try {
      const res = await api.get(`/members/${member.id}`);
      setMemberProfile(res.data);
    } catch {
      setMemberProfile(member); // Fallback to card data
    } finally {
      setProfileLoading(false);
    }
  };

  const closeMemberProfile = () => {
    setSelectedMember(null);
    setMemberProfile(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="members-title">Member Directory</h1>
          <p className="text-slate-600 text-sm mt-1">Browse and connect with drivers, carriers, dispatchers, learners, service providers, and affiliates</p>
        </div>

        {/* Search & Filter */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by name..."
                className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="member-search-input"
              />
            </div>
            <div className="relative flex-1 sm:max-w-[220px]">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={locationSearch}
                onChange={(e) => handleLocationSearch(e.target.value)}
                placeholder="City or state..."
                className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="member-location-input"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap mt-3">
            {ROLE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleRoleFilter(opt.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roleFilter === opt.value 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">{total} members found</p>
        </div>

        {/* Members Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-slate-200" />
                  <div className="flex-1"><div className="h-4 bg-slate-200 rounded w-2/3 mb-2" /><div className="h-3 bg-slate-100 rounded w-1/2" /></div>
                </div>
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No members found</p>
            <p className="text-sm text-slate-400">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((member, idx) => {
              const style = ROLE_STYLES[member.role] || ROLE_STYLES.driver;
              const RIcon = style.icon;
              const vStatus = VERIFICATION_STYLES[member.verification_status] || VERIFICATION_STYLES.unverified;
              const VIcon = vStatus.icon;
              const loc = member.location || [member.city, member.state].filter(Boolean).join(', ');
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  onClick={() => openMemberProfile(member)}
                  className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                  data-testid={`member-${member.id}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {member.profile_picture ? (
                        <img src={mediaUrl(member.profile_picture)} alt={member.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-6 w-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate">{member.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${style.bg} ${style.color}`}>
                          <RIcon className="h-3 w-3" />{style.label}
                        </span>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs ${vStatus.bg} ${vStatus.color}`}>
                          <VIcon className="h-3 w-3" />{vStatus.label}
                        </span>
                      </div>
                    </div>
                  </div>
                  {loc && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                      <MapPin className="h-3 w-3" />{loc}
                    </div>
                  )}
                  {member.bio && (
                    <p className="text-xs text-slate-500 line-clamp-2">{member.bio}</p>
                  )}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-400 capitalize">{member.membership_tier} tier</span>
                    <span className="text-xs text-slate-400">Joined {new Date(member.created_at).toLocaleDateString()}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40" data-testid="prev-page">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40" data-testid="next-page">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </main>
      <Footer />

      {/* Full Mini Profile Popup */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto"
            onClick={closeMemberProfile}
            data-testid="member-popup-overlay"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="min-h-full flex items-start justify-center py-6 px-4">
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 30 }}
                transition={{ type: 'spring', damping: 25 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="member-popup"
              >
                {profileLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : (() => {
                  const m = memberProfile || selectedMember;
                  const style = ROLE_STYLES[m.role] || ROLE_STYLES.driver;
                  const RIcon = style.icon;
                  const vStatus = VERIFICATION_STYLES[m.verification_status] || VERIFICATION_STYLES.unverified;
                  const VIcon = vStatus.icon;
                  const loc = m.location || [m.city, m.state].filter(Boolean).join(', ');

                  return (
                    <>
                      {/* Header */}
                      <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 px-6 pt-5 pb-14">
                        <button onClick={closeMemberProfile}
                          className="absolute top-3 right-3 p-2 bg-white/15 hover:bg-white/30 text-white rounded-full transition-colors z-10"
                          data-testid="close-member-popup">
                          <X className="h-5 w-5" />
                        </button>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold ${style.bg} ${style.color}`}>
                            <RIcon className="h-3.5 w-3.5" />{style.label}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold ${vStatus.bg} ${vStatus.color}`}>
                            <VIcon className="h-3.5 w-3.5" />{vStatus.label}
                          </span>
                          {m.open_to_work && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-700">
                              <Briefcase className="h-3.5 w-3.5" />Open to Work
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-white/10 text-slate-300">
                            <Crown className="h-3.5 w-3.5" /><span className="capitalize">{m.membership_tier || 'Free'} Tier</span>
                          </span>
                        </div>
                      </div>

                      {/* Avatar + Name */}
                      <div className="px-6 -mt-10 mb-4 flex items-end gap-4">
                        <div className="w-20 h-20 rounded-full border-4 border-white bg-slate-100 flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0">
                          {m.profile_picture ? (
                            <img src={mediaUrl(m.profile_picture)} alt={m.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-10 w-10 text-slate-400" />
                          )}
                        </div>
                        <div className="pb-1 flex-1 min-w-0">
                          <h3 className="text-xl font-bold text-slate-900 leading-tight truncate">{m.name}</h3>
                          {m.company_name && <p className="text-sm text-slate-500 truncate">{m.company_name}</p>}
                        </div>
                      </div>

                      {/* Truck Picture */}
                      {m.truck_picture && (
                        <div className="px-6 mb-4">
                          <img src={mediaUrl(m.truck_picture)} alt="Truck" className="w-full h-32 object-cover rounded-lg border border-slate-200" />
                        </div>
                      )}

                      {/* Bio */}
                      {m.bio && (
                        <div className="px-6 mb-4">
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">About</h4>
                          <p className="text-sm text-slate-700 leading-relaxed">{m.bio}</p>
                        </div>
                      )}

                      {/* Contact & Details */}
                      <div className="px-6 mb-4">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Contact & Details</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {loc && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><MapPin className="h-4 w-4 text-blue-500" /></div>
                              <div><p className="text-[10px] text-slate-400">Location</p><p className="text-sm text-slate-900 font-medium">{loc}</p></div>
                            </div>
                          )}
                          {m.email && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4 text-emerald-500" /></div>
                              <div><p className="text-[10px] text-slate-400">Email</p><p className="text-sm text-slate-900 font-medium truncate">{m.email}</p></div>
                            </div>
                          )}
                          {m.phone && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0"><Phone className="h-4 w-4 text-violet-500" /></div>
                              <div><p className="text-[10px] text-slate-400">Phone</p><p className="text-sm text-slate-900 font-medium">{m.phone}</p></div>
                            </div>
                          )}
                          {m.dot_number && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><Hash className="h-4 w-4 text-amber-500" /></div>
                              <div><p className="text-[10px] text-slate-400">DOT Number</p><p className="text-sm text-slate-900 font-medium">{m.dot_number}</p></div>
                            </div>
                          )}
                          {m.mc_number && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0"><Briefcase className="h-4 w-4 text-rose-500" /></div>
                              <div><p className="text-[10px] text-slate-400">MC Number</p><p className="text-sm text-slate-900 font-medium">{m.mc_number}</p></div>
                            </div>
                          )}
                          {m.referral_code && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0"><Hash className="h-4 w-4 text-cyan-500" /></div>
                              <div><p className="text-[10px] text-slate-400">Referral Code</p><p className="text-sm text-slate-900 font-mono">{m.referral_code}</p></div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Professional Info */}
                      {(m.experience_years || m.license_type || m.fleet_size || m.years_in_business || m.insurance_provider) && (
                        <div className="px-6 mb-4">
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Professional Info</h4>
                          <div className="grid grid-cols-2 gap-3">
                            {m.experience_years && (
                              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                <p className="text-[10px] text-slate-400">Experience</p>
                                <p className="text-sm font-semibold text-slate-900">{m.experience_years} years</p>
                              </div>
                            )}
                            {m.license_type && (
                              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                <p className="text-[10px] text-slate-400">License</p>
                                <p className="text-sm font-semibold text-slate-900">{m.license_type}</p>
                              </div>
                            )}
                            {m.fleet_size && (
                              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                <p className="text-[10px] text-slate-400">Fleet Size</p>
                                <p className="text-sm font-semibold text-slate-900">{m.fleet_size} vehicles</p>
                              </div>
                            )}
                            {m.years_in_business && (
                              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                <p className="text-[10px] text-slate-400">In Business</p>
                                <p className="text-sm font-semibold text-slate-900">{m.years_in_business} years</p>
                              </div>
                            )}
                            {m.insurance_provider && (
                              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 col-span-2">
                                <p className="text-[10px] text-slate-400">Insurance</p>
                                <p className="text-sm font-semibold text-slate-900">{m.insurance_provider}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Service Areas / Equipment / Specializations */}
                      {(m.service_areas || m.equipment_types || m.specializations) && (
                        <div className="px-6 mb-4">
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Capabilities</h4>
                          {m.service_areas && (
                            <div className="mb-3">
                              <p className="text-[10px] text-slate-400 mb-1.5 flex items-center gap-1"><Globe className="h-3 w-3" />Service Areas</p>
                              <div className="flex flex-wrap gap-1.5">
                                {(Array.isArray(m.service_areas) ? m.service_areas : [m.service_areas]).map((a, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{a}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {m.equipment_types && (
                            <div className="mb-3">
                              <p className="text-[10px] text-slate-400 mb-1.5 flex items-center gap-1"><Truck className="h-3 w-3" />Equipment</p>
                              <div className="flex flex-wrap gap-1.5">
                                {(Array.isArray(m.equipment_types) ? m.equipment_types : [m.equipment_types]).map((e, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">{e}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {m.specializations && (
                            <div>
                              <p className="text-[10px] text-slate-400 mb-1.5 flex items-center gap-1"><Star className="h-3 w-3" />Specializations</p>
                              <div className="flex flex-wrap gap-1.5">
                                {(Array.isArray(m.specializations) ? m.specializations : [m.specializations]).map((s, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">{s}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Badges & Credentials */}
                      {m.badges && m.badges.length > 0 && (
                        <div className="px-6 mb-4">
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Credentials</h4>
                          <div className="flex flex-wrap gap-2">
                            {m.badges.map((b, i) => (
                              <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${
                                b.status === 'verified' ? 'bg-green-50 text-green-700 border-green-200' :
                                b.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                'bg-red-50 text-red-700 border-red-200'
                              }`}>
                                {b.status === 'verified' ? <CheckCircle className="h-3.5 w-3.5" /> :
                                 b.status === 'pending' ? <Clock className="h-3.5 w-3.5" /> :
                                 <Shield className="h-3.5 w-3.5" />}
                                {b.badge_type}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <Calendar className="h-3.5 w-3.5" />Joined {new Date(m.created_at).toLocaleDateString()}
                        </div>
                        {m.email && (
                          <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors">
                            <Mail className="h-3.5 w-3.5" />Contact
                          </a>
                        )}
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Members;
